import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import useUserContext from '../../../hooks/useUserContext'
import API from '../../../utils/api'
import utils from '../../../utils/utils'
import { buildCashoutFormData } from '../utils/build-cashout-form-data'
import { getApiErrorMessage } from '../utils/get-api-error-message'
import { parseAmount } from '../validations/parse-amount'
import { toastSuccess } from '../../Toast'

export const useSubmitCashout = ({
  methods,
  user,
  banks,
  lastPurchaseBank,
}) => {
  const queryClient = useQueryClient()
  const { updateUserBalance } = useUserContext()
  const [successMessage, setSuccessMessage] = React.useState(null)

  const mutation = useMutation({
    mutationFn: async (values) => {
      const phoneRaw = user?.phone
      const phone = phoneRaw === '' || phoneRaw == null ? '' : String(phoneRaw)

      // Para Debito y Credito
      let account = values.method === 'spei' ? values.clabe : values.card_number

      // Para SPEI
      if (!account && values.method === 'spei') {
        account = values.account
      }

      // Para Debito cuando agrega una nueva
      if (values.cashout_account_id === 'OTHER') {
        if (values.method === 'spei') {
          account = values.clabe
        } else if (values.method === 'card') {
          account = values.card_number
        }
      }

      // bank y bank_code:
      // priorizar values.bank / values.bank_code; si faltan, resolverlos con bank_id en banks.
      const bankMeta = banks?.find(({ bank_code }) =>
        String(bank_code) === values.bank_id
      )

      const bank = values.bank || bankMeta?.bank
      const bank_code = values.bank_code || bankMeta?.bank_code

      const amount = parseAmount(values.amount)
      if (!Number.isFinite(amount)) {
        methods.setError('root', { message: 'El monto no es válido' })
        return { ok: false, data: { message: 'El monto no es válido' }, values }
      }

      const basePayload = {
        variation: 'normal',
        amount,
        password: values.password,
        phone,
        bank: String(bank ?? ''),
        bank_code: String(bank_code ?? ''),
        name: values.name,
        last_name: values.last_name,
      }

      const getIsSavedAccount = () => {
        return (
          values.cashout_account_id &&
           values.cashout_account_id !== 'OTHER'
        )
      }

      let documents = {
        csf: values.csf instanceof File ? values.csf : null,
      }

      ;['card', 'csf', 'bank_statement'].forEach((key) => {
        if (values[key]) {
          documents[key] = values[key]
        }
      })

      const requestData = getIsSavedAccount()
        // Si es una tarjeta o cuenta guardada y verificada
        ? {
          ...basePayload,
          cashout_account_id: values.cashout_account_id,
          documents,
        }
        // Si es nueva tarjeta o cuenta CLABE
        : {
          ...basePayload,
         
          method: values.method,
          account,
          documents,
        }

      const fd = buildCashoutFormData(requestData)

      const { data } = await API.postCashout(fd)

      // Error de negocio: NO throw para evitar overlay en desarrollo
      if (!data?.success) {
        return { ok: false, data, values }
      }

      return { ok: true, data, values }
    },

    onMutate: () => {
      methods.clearErrors('root')
      setSuccessMessage(null)
    },

    onSuccess: async (result) => {
      if (!result?.ok) {
        const message = getApiErrorMessage(result?.data, 'No se pudo completar la solicitud')
        methods.setError('root', { message })
        return
      }

      const { values } = result

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/cashouts/available'] }),
        queryClient.invalidateQueries({ queryKey: ['/cashout-accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['/cashouts/history'] }),
      ])

      methods.reset({
        amount: values.amount,
        method: values.method ?? 'spei',
        name: user?.name,
        last_name: user?.last_name,
        bank_id: String(values.bank_id ?? ''),
        cashout_account_id: values.cashout_account_id ?? '',
        clabe: values.clabe,
        password: '',
      })

      setSuccessMessage(
        'Tu solicitud fue enviada. En breve te notificaremos sobre el estado de tu retiro.'
      )

      toastSuccess('Tu solicitud fue enviada exitosamente.')

      if (result.data?.balance != null) {
        const userBalance = utils.getUserBalance()
        updateUserBalance({
          ...userBalance,
          balanceReal: Number(result.data.balance),
        })
      }
    },

    onError: (err) => {
      // Aquí solo caen errores técnicos (HTTP/network/unexpected)
      const message = getApiErrorMessage(
        err,
        'Sucedió un error inesperado, por favor inténtalo nuevamente.'
      )
      methods.setError('root', { message })
    },
  })

  return {
    submitCashout: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
    successMessage,
    resetSubmitState: mutation.reset,
  }
}